// orb-choice-cast.ts — the cast-time picker for `channel_chosen_orb` (2026-09-09).
//
// THE SHAPE IT SERVES. "Channel 3 orbs of your choosing" (Overclocking) / "Instantly channel 10
// orbs of your choice" (Core Dominion). The author sets only HOW MANY; the player names the slugs
// here, at cast. This is the third channel shape — channel_orb is author-picked and
// channel_random_orb is engine-rolled, and neither could express a pick made against the board in
// front of you.
//
// WHY IT LIVES IN THIS BUNDLE. The picks ride use_ability_variable_2 as a slug CSV, so the modal
// has to hand its result to useAbilityVariable2() — which is defined in use-ability.ts, the
// sibling module in this same encounter-overlays bundle. Resolved off `window` at click time
// (not imported) because use-ability.ts publishes its surface through Object.assign(w, …) rather
// than exports, exactly as aoe-range.ts reaches it.
//
// THE CLIENT IS SUGAR. ft_resolve_chosen_orb_picks() (janus/encounter/orb_queue.php) re-authorizes
// every pick against the OrbType catalog and TRUNCATES to the authored count, so nothing offered
// here can widen what the author allowed — and a cancelled/absent pick is not a dead cast, it
// rolls the count at random server-side.
//
// REPEATS ARE LEGAL AND NECESSARY: Core Dominion channels 10 orbs from a catalog of 8.

const w = window as unknown as Record<string, unknown>;

/** OrbType::LIST (additions.php) is the source of truth for slugs, names and order; the colors are
 *  the token-arc palette verbatim (creature-render/creature-thumbnail.ts buildOrbArcHTML, mirrored
 *  again in player-character-sheet/tabs/weapon-abilities.ts ORB_QUEUE_COLORS). Three copies rather
 *  than one shared module because the three live in three separate bundles; keep them in step. */
const ORB_CHOICES: ReadonlyArray<{ slug: string; name: string; color: string }> = [
    { slug: 'lightning', name: 'Lightning Orb', color: '#f5d90a' },
    { slug: 'cold',      name: 'Cold Orb',      color: '#6ec6ff' },
    { slug: 'necrotic',  name: 'Necrotic Orb',  color: '#8b2fd6' },
    { slug: 'thunder',   name: 'Thunder Orb',   color: '#4757d6' },
    { slug: 'fire',      name: 'Fire Orb',      color: '#ff7a1a' },
    { slug: 'force',     name: 'Force Orb',     color: '#c08cff' },
    { slug: 'poison',    name: 'Poison Orb',    color: '#35c235' },
    { slug: 'acid',      name: 'Acid Orb',      color: '#9acd32' },
];

/** Mirrors FT_CHANNEL_ORB_QTY_MAX (orb_queue.php) — the server clamps to the same ceiling. */
const CHANNEL_ORB_QTY_MAX = 20;

const MODAL_ID = 'ft_orb_choice_modal';

function orbColor(slug: string): string {
    return ORB_CHOICES.find((o) => o.slug === slug)?.color ?? '#ffffff';
}

/** One row: "1." + a select of every orb type + a live pip in that orb's color. */
function buildRow(index: number): { row: HTMLElement; select: HTMLSelectElement } {
    const row = document.createElement('div');
    row.className = 'd-flex align-items-center gap-2 mb-2';

    const label = document.createElement('span');
    label.className = 'text-muted small';
    label.style.minWidth = '1.5rem';
    label.textContent = `${index + 1}.`;
    row.appendChild(label);

    const select = document.createElement('select');
    select.className = 'form-select form-select-sm';
    select.setAttribute('aria-label', `Orb ${index + 1}`);
    for (const orb of ORB_CHOICES) {
        const option = document.createElement('option');
        option.value = orb.slug;
        option.textContent = orb.name;
        select.appendChild(option);
    }
    row.appendChild(select);

    const pip = document.createElement('span');
    pip.style.cssText = 'display:inline-block;width:14px;height:14px;border-radius:50%;flex-shrink:0;';
    const paint = () => {
        const color = orbColor(select.value);
        pip.style.background = `radial-gradient(circle at 35% 30%, #ffffffcc 0%, ${color} 45%, ${color}33 100%)`;
        pip.style.boxShadow = `0 0 5px 1px ${color}88`;
    };
    paint();
    select.addEventListener('change', paint);
    row.appendChild(pip);

    return { row, select };
}

function ensureModal(): HTMLElement {
    const existing = document.getElementById(MODAL_ID);
    if (existing) return existing;
    const el = document.createElement('div');
    el.id = MODAL_ID;
    el.className = 'modal fade';
    // FT3D's fullscreen scene (19980) and HUD/drawers (20000–20003) cover Bootstrap's
    // default modal layer (1050). Keep this picker and its scrim above that chrome,
    // including the manual-show fallback where Bootstrap supplies no backdrop.
    el.style.zIndex = '20010';
    el.style.backgroundColor = 'rgba(0, 0, 0, 0.45)';
    el.tabIndex = -1;
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = `
        <div class="modal-dialog modal-sm modal-dialog-centered">
            <div class="modal-content bg-dark text-light">
                <div class="modal-header">
                    <h5 class="modal-title mb-0" data-ft-orb-choice-title>Choose your Orbs</h5>
                    <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Cancel"></button>
                </div>
                <div class="modal-body" data-ft-orb-choice-body></div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-sm btn-secondary" data-bs-dismiss="modal" data-ft-orb-choice-cancel>Cancel</button>
                    <button type="button" class="btn btn-sm btn-info" data-ft-orb-choice-confirm>Channel</button>
                </div>
            </div>
        </div>`;
    document.body.appendChild(el);
    return el;
}

/** Show the modal. Bootstrap is a CDN global here (same posture as object-card/mesh-gallery.ts);
 *  without it the markup would sit inert, so fall back to showing it by hand. */
function showModal(el: HTMLElement): void {
    const bs = (window as unknown as {
        bootstrap?: { Modal?: { getOrCreateInstance(node: Element): { show(): void; hide(): void } } };
    }).bootstrap;
    if (bs?.Modal) { bs.Modal.getOrCreateInstance(el).show(); return; }
    el.classList.add('show');
    el.style.display = 'block';
}

function hideModal(el: HTMLElement): void {
    const bs = (window as unknown as {
        bootstrap?: { Modal?: { getOrCreateInstance(node: Element): { show(): void; hide(): void } } };
    }).bootstrap;
    if (bs?.Modal) { bs.Modal.getOrCreateInstance(el).hide(); return; }
    el.classList.remove('show');
    el.style.display = 'none';
}

/**
 * Teardown for the currently-open pick session, or null when nothing is open.
 *
 * ONE MODAL ELEMENT SERVES EVERY ABILITY, so a second open before the first closes used to leave
 * the first session's confirm listener attached: one Channel click then fired BOTH casts, the
 * older one reading its now-detached selects, while the visible dialog showed only the newer
 * request (Codex review 2026-09-09, confirmed bug #2). Relying on the fired-listener removal or
 * on `hidden.bs.modal` cannot cover that — neither event happens between two opens — so every
 * open explicitly ends the previous session first.
 */
let endOpenSession: (() => void) | null = null;

/**
 * Open the pick modal for `abilityId` and, on confirm, fire the cast with the chosen slugs on
 * use_ability_variable_2. Called from the ability button's generated snippet (ability-button.ts),
 * which is also what the action bar replays via data-ft-use-snippet — so the pick is offered on
 * every surface that casts the ability, not just the sheet.
 *
 * A non-positive count is a mis-wire, not a cast with no picks: fall through to the plain cast so
 * the ability still fires (the server would roll the count anyway).
 */
function ftOrbChoiceCast(item_id_used: unknown, orb_count: unknown): void {
    const abilityId = String(item_id_used ?? '');
    const count = Math.min(CHANNEL_ORB_QTY_MAX, Math.max(0, Math.floor(Number(orb_count)) || 0));
    if (!abilityId) return;
    if (count < 1) {
        (w.useAbility as ((id: string) => void) | undefined)?.(abilityId);
        return;
    }

    // End the previous session BEFORE this open rebuilds the shared body — see endOpenSession.
    endOpenSession?.();

    const modal = ensureModal();
    const title = modal.querySelector('[data-ft-orb-choice-title]');
    const body = modal.querySelector('[data-ft-orb-choice-body]');
    const confirm = modal.querySelector('[data-ft-orb-choice-confirm]');
    if (!body || !confirm) return;

    if (title) title.textContent = count === 1 ? 'Choose your Orb' : `Choose ${count} Orbs`;

    const selects: HTMLSelectElement[] = [];
    body.textContent = '';

    const summary = document.createElement('p');
    summary.className = 'text-muted small mb-2';
    summary.textContent = count === 1
        ? 'Pick the orb to channel.'
        : `Pick ${count} orbs to channel, in order. The same orb may be picked more than once.`;
    body.appendChild(summary);

    for (let i = 0; i < count; i++) {
        const { row, select } = buildRow(i);
        selects.push(select);
        body.appendChild(row);
    }

    // One cast per session, whatever the user clicks: `spent` closes the door even if a stray
    // duplicate listener survived, and teardown detaches everything this session installed.
    let spent = false;
    const cancels = Array.from(modal.querySelectorAll<HTMLElement>('[data-bs-dismiss="modal"]'));

    const teardown = () => {
        confirm.removeEventListener('click', onConfirm);
        for (const button of cancels) button.removeEventListener('click', onCancel);
        modal.removeEventListener('hidden.bs.modal', teardown);
        if (endOpenSession === teardown) endOpenSession = null;
    };

    function onCancel(): void {
        if (spent) return;
        spent = true;
        // Bootstrap's own data-bs-dismiss closes the dialog; without Bootstrap those attributes are
        // inert and Cancel would leave the modal stuck open (Codex review 2026-09-09, confirmed
        // bug #3), so close it by hand — hideModal() is a no-op-safe double-close when BS is here.
        hideModal(modal);
        teardown();
    }

    function onConfirm(): void {
        if (spent) return;
        spent = true;
        hideModal(modal);
        teardown();
        const picks = selects.map((select) => select.value).filter((value) => value !== '');
        const cast = w.useAbilityVariable2 as
            ((id: string, v1: string, v2: string) => void) | undefined;
        if (cast) { cast(abilityId, '', picks.join(',')); return; }
        // useAbilityVariable2 missing means the bundle failed to load — cast unpicked rather than
        // swallowing the click; the server rolls the count.
        (w.useAbility as ((id: string) => void) | undefined)?.(abilityId);
    }

    endOpenSession = teardown;
    confirm.addEventListener('click', onConfirm);
    for (const button of cancels) button.addEventListener('click', onCancel);
    // Covers the ESC key and a backdrop click, which fire neither button.
    modal.addEventListener('hidden.bs.modal', teardown);

    showModal(modal);
}

// WINDOW EXPORTS — ftOrbChoiceCast is named in a generated inline snippet, so it must be global.
Object.assign(w, { ftOrbChoiceCast });

export {};
